package org.teddapp.project.ui.mindmap

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import org.teddapp.project.ui.TeddMenu
import org.teddapp.project.ui.TeddMenuEntry
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Mind_map
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.teddapp.project.card.TeddShare
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.LocalTeddColors
import org.teddapp.project.ui.TeddBottomSheet
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddSnackbarState
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.rememberAfterFrame
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.NavIntent
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.SettingsDrawerHost
import org.teddapp.project.ui.settings.rememberSettingsDrawer

/**
 * Cartes heuristiques : la liste des cartes, et une carte ouverte sur un canevas qu'on
 * parcourt, plie, zoome et modifie. Grand écran : liste à gauche, canevas au centre,
 * panneau du nœud à droite. Téléphone : la liste, puis la carte plein écran avec une
 * barre d'actions sous le pouce et les notes dans une feuille.
 */

/** Le panneau du nœud, à droite de la carte : 320 dp par défaut, de 260 à 560. */
private val NODE_PANE = org.teddapp.project.ui.PaneSpec("mindmap.node", default = 320.dp, min = 260.dp, max = 560.dp)

@Composable
fun MindMapScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val model = rememberMindMapModel(repos)
    val snackbar = remember { TeddSnackbarState() }
    val drawer = rememberSettingsDrawer()
    val camera = rememberMindMapCamera()
    val palette = rememberMindMapPalette(model.map?.color)
    val measurer = androidx.compose.ui.text.rememberTextMeasurer()
    val density = LocalDensity.current
    val body = TeddType.Body
    val colors = LocalTeddColors.current
    val ink = TeddTokens.Ink
    val onAction = TeddTokens.OnAction
    val action = TeddTokens.Action
    val bg = TeddTokens.Bg
    val lime = TeddTokens.Lime
    val painter = remember(measurer, density, palette, colors) {
        MindMapPainter(measurer, density, body, palette, ink, onAction, action, bg, lime)
    }
    var pendingDelete by remember { mutableStateOf<String?>(null) }

    val openSource: (String, String) -> Unit = { kind, id ->
        when (kind) {
            "objective", "milestone" -> { NavIntent.openObjective(id); onNavigate(Section.Objectives) }
            "project" -> { NavIntent.openProject(id); onNavigate(Section.Projects) }
            else -> { model.map?.skill_id?.let { NavIntent.openSkill(it) }; onNavigate(Section.Skills) }
        }
    }
    val requestDelete: (String) -> Unit = { id ->
        if (model.deletionWeight(id) > 0) pendingDelete = id
        else model.deleteNode(id) { snackbar.show("Branche supprimée") }
    }

    WithWindowClass(modifier.fillMaxSize()) { wc ->
        if (wc == WindowClass.Compact) {
            Column(Modifier.fillMaxSize().background(TeddTokens.Bg).statusBarsPadding()) {
                Box(Modifier.weight(1f).fillMaxWidth()) {
                    if (model.openMapId == null) {
                        MapList(model, compact = true, Modifier.fillMaxSize())
                    } else {
                        CompactMap(model, camera, painter, snackbar, openSource, requestDelete)
                    }
                    TeddSnackbarHost(snackbar, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 16.dp))
                }
                if (model.openMapId == null) TeddBottomNav(active = Section.MindMaps, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
            }
        } else {
            SettingsDrawerHost(drawer, onNavigate = onNavigate) {
                Row(Modifier.fillMaxSize().background(TeddTokens.Bg)) {
                    TeddNavRail(active = Section.MindMaps, onNavigate = onNavigate, onSettingsClick = drawer::open)
                    val showList = wc == WindowClass.Expanded || model.openMapId == null
                    if (showList) {
                        MapList(model, compact = false, Modifier.width(if (wc == WindowClass.Expanded) 280.dp else 240.dp).fillMaxHeight())
                        Box(Modifier.width(1.dp).fillMaxHeight().background(TeddTokens.Line))
                    }
                    Box(Modifier.weight(1f).fillMaxHeight()) {
                        if (model.openMapId == null) {
                            NoMapOpen(model)
                        } else {
                            Column(Modifier.fillMaxSize()) {
                                MapToolbar(model, camera, painter, snackbar, compact = false, onBack = if (wc == WindowClass.Expanded) null else { { model.close() } })
                                Row(Modifier.weight(1f).fillMaxWidth()) {
                                    Box(Modifier.weight(1f).fillMaxHeight()) {
                                        MindMapCanvas(model, camera, LayoutMode.Radial, painter, Modifier.fillMaxSize(), onOpenNotes = { model.select(it); model.notesOpen = true }, onRequestDelete = requestDelete)
                                        CanvasOverlays(model, camera, compact = false)
                                    }
                                    val selected = model.selectedId
                                    if (selected != null && (wc == WindowClass.Expanded || model.notesOpen)) {
                                        // Le filet est la poignée : on y règle la largeur du panneau du nœud.
                                        org.teddapp.project.ui.TeddSidePane(
                                            spec = NODE_PANE, side = org.teddapp.project.ui.PaneSide.Right, title = "le nœud", icon = "notes",
                                            contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp),
                                        ) {
                                            MindMapNodePanel(model, selected, compact = false, onOpenSource = openSource, onRequestDelete = requestDelete, onClose = { model.select(null); model.notesOpen = false }, modifier = Modifier.fillMaxSize().background(TeddTokens.Surface))
                                        }
                                    }
                                }
                            }
                        }
                        TeddSnackbarHost(snackbar, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 24.dp))
                    }
                }
            }
        }
    }

    if (model.creating) CreateMapDialog(model, onClose = { model.creating = false })
    pendingDelete?.let { id ->
        ConfirmDeleteDialog(
            weight = model.deletionWeight(id),
            onConfirm = { model.deleteNode(id) { snackbar.show("Branche supprimée") }; pendingDelete = null },
            onClose = { pendingDelete = null },
        )
    }
}

// ── Liste des cartes ──────────────────────────────────────────────────────────

@Composable
private fun MapList(model: MindMapModel, compact: Boolean, modifier: Modifier) {
    Column(modifier.padding(horizontal = if (compact) TeddSpace.S4 else TeddSpace.S4, vertical = TeddSpace.S4), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        // Le titre est long et la colonne étroite : le bouton va sous le titre, pas à côté.
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Text("Cartes heuristiques", style = TeddType.Title1, color = TeddTokens.Ink)
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                Text("Relie ce que tu apprends.", style = TeddType.Body, color = TeddTokens.InkMuted, modifier = Modifier.weight(1f))
                TeddButton(text = "Nouvelle", onClick = { model.creating = true }, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm, icon = "plus")
            }
        }
        if (model.maps.isEmpty()) {
            EmptyMaps(model)
        } else {
            LazyColumn(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2), modifier = Modifier.weight(1f)) {
                items(model.maps, key = { it.id }) { map -> MapRow(model, map, selected = map.id == model.openMapId) }
            }
        }
    }
}

@Composable
private fun MapRow(model: MindMapModel, map: Mind_map, selected: Boolean) {
    val skill = map.skill_id?.let { id -> model.skills.firstOrNull { it.id == id } }
    val objective = map.objective_id?.let { id -> model.objectives.firstOrNull { it.id == id } }
    val tint = map.color?.let(::parseHexColor) ?: TeddTokens.ModNotes
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Row(
        modifier = Modifier.fillMaxWidth()
            .background(if (selected) TeddTokens.SurfaceSunken else TeddTokens.Surface, shape)
            .border(1.dp, if (selected) TeddTokens.LineStrong else TeddTokens.Line, shape)
            .clickable { model.open(map.id) }
            .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S3),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        Box(Modifier.width(10.dp).height(28.dp).background(tint, RoundedCornerShape(TeddTokens.RFull)))
        Column(Modifier.weight(1f)) {
            Text(map.title, style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis)
            val sub = listOfNotNull(skill?.let { "Compétence : ${it.name}" }, objective?.let { "Objectif : ${it.title}" }).joinToString(" · ")
            if (sub.isNotEmpty()) Text(sub, style = TeddType.Caption, color = TeddTokens.InkMuted, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
        TeddIcon(name = "chevron-right", size = 14.dp, color = TeddTokens.InkSubtle)
    }
}

/** Trois gabarits pour démarrer vite ; chaque branche se renomme ensuite. */
private val TEMPLATES = listOf(
    Triple("Sujet de cours", "Un cours", listOf("Notions clés", "Exemples", "Questions", "À réviser")),
    Triple("Livre", "Un livre", listOf("Idée centrale", "Arguments", "Citations", "Ce que j'en fais")),
    Triple("Projet", "Un projet", listOf("Objectif", "Étapes", "Risques", "Ressources")),
)

@Composable
private fun EmptyMaps(model: MindMapModel) {
    Column(Modifier.fillMaxWidth().padding(top = TeddSpace.S4), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Text("Ta première carte", style = TeddType.Title2, color = TeddTokens.Ink)
        Text(
            "Un sujet au centre, des branches autour, des notes dans les nœuds. Pars d'une page blanche, d'un gabarit, ou convertis une compétence depuis sa fiche.",
            style = TeddType.Body, color = TeddTokens.InkMuted,
        )
        TeddButton(text = "Nouvelle carte", onClick = { model.creating = true }, kind = TeddButtonKind.Primary, icon = "plus")
        Text("GABARITS", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(top = TeddSpace.S2))
        TEMPLATES.forEach { (name, title, branches) ->
            Row(
                modifier = Modifier.fillMaxWidth()
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                    .clickable { model.createFromTemplate(title, branches) }
                    .padding(TeddSpace.S3),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
            ) {
                TeddIcon(name = "mindmap", size = 16.dp, color = TeddTokens.ModNotes)
                Column(Modifier.weight(1f)) {
                    Text(name, style = TeddType.BodyStrong, color = TeddTokens.Ink)
                    Text(branches.joinToString(" · "), style = TeddType.Caption, color = TeddTokens.InkMuted, maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
            }
        }
    }
}

@Composable
private fun NoMapOpen(model: MindMapModel) {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(TeddSpace.S3), modifier = Modifier.widthIn(max = 420.dp).padding(TeddSpace.S6)) {
            TeddIcon(name = "mindmap", size = 40.dp, color = TeddTokens.InkSubtle, strokeWidth = 1.25.dp)
            Text(if (model.maps.isEmpty()) "Aucune carte pour l'instant" else "Choisis une carte", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                "Tab ajoute un enfant, Maj+Tab un frère, Entrée modifie, Espace plie, les flèches se déplacent, Ctrl+molette zoome.",
                style = TeddType.Caption, color = TeddTokens.InkMuted, textAlign = androidx.compose.ui.text.style.TextAlign.Center,
            )
            if (model.maps.isEmpty()) TeddButton(text = "Nouvelle carte", onClick = { model.creating = true }, kind = TeddButtonKind.Primary, icon = "plus")
        }
    }
}

// ── Barre d'outils et surcouches du canevas ───────────────────────────────────

@Composable
private fun MapToolbar(model: MindMapModel, camera: MindMapCamera, painter: MindMapPainter, snackbar: TeddSnackbarState, compact: Boolean, onBack: (() -> Unit)?) {
    val map = model.map ?: return
    val scope = rememberCoroutineScope()
    var menu by remember { mutableStateOf(false) }
    var attach by remember { mutableStateOf(false) }
    val layer = rememberGraphicsLayer()
    val density = LocalDensity.current
    val bg = TeddTokens.Bg
    Row(
        modifier = Modifier.fillMaxWidth().background(TeddTokens.Surface).padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        if (onBack != null) TeddIconButton(onClick = onBack) { TeddIcon(name = "arrow-left", size = 16.dp, color = TeddTokens.Ink) }
        Column(Modifier.weight(1f)) {
            Text(map.title, style = TeddType.Heading, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis)
            val skill = map.skill_id?.let { id -> model.skills.firstOrNull { it.id == id } }
            val objective = map.objective_id?.let { id -> model.objectives.firstOrNull { it.id == id } }
            val sub = buildList {
                skill?.let { add("Compétence : ${it.name}") }
                objective?.let { add("Objectif : ${it.title}") }
                add(if (model.totalCount == 1) "1 nœud" else "${model.totalCount} nœuds")
                if (model.revision) add("révision : ${model.revealedCount} / ${model.totalCount} révélés")
            }.joinToString(" · ")
            Text(sub, style = TeddType.Caption, color = TeddTokens.InkMuted, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
        // Ajouter un nœud sous la sélection (ou sous le sujet central) : le geste de base, visible.
        TeddButton(
            text = if (compact) "Nœud" else "Ajouter un nœud",
            onClick = { model.addChild(); if (compact) model.notesOpen = true },
            kind = TeddButtonKind.Primary,
            size = TeddButtonSize.Sm,
            icon = "plus",
            kbd = if (compact) null else "Tab",
        )
        if (!compact) {
            if (model.searchOpen) {
                SearchBox(model, Modifier.width(240.dp))
            } else {
                TeddIconButton(onClick = { model.searchOpen = true }) { TeddIcon(name = "search", size = 16.dp, color = TeddTokens.Ink) }
            }
            TeddIconButton(onClick = { model.collapseAll() }) { TeddIcon(name = "fold", size = 16.dp, color = TeddTokens.Ink) }
            TeddIconButton(onClick = { model.expandAll() }) { TeddIcon(name = "unfold", size = 16.dp, color = TeddTokens.Ink) }
            if (TeddShare.shareImage != null) {
                TeddIconButton(onClick = {
                    scope.launch {
                        runCatching {
                            val image = exportMindMapImage(layer, model.layout, painter, density, bg, model.noteCounts)
                            TeddShare.shareImage?.invoke(exportFileName(map.title), image)
                        }.onSuccess { it?.let(snackbar::show) }.onFailure { snackbar.show("L'image n'a pas pu être créée") }
                    }
                }) { TeddIcon(name = "image", size = 16.dp, color = TeddTokens.Ink) }
            }
        }
        Box {
            TeddIconButton(onClick = { menu = true }) { TeddIcon(name = "more", size = 16.dp, color = TeddTokens.Ink) }
            val export: () -> Unit = {
                scope.launch {
                    runCatching {
                        val image = exportMindMapImage(layer, model.layout, painter, density, bg, model.noteCounts)
                        TeddShare.shareImage?.invoke(exportFileName(map.title), image)
                    }.onSuccess { it?.let(snackbar::show) }.onFailure { snackbar.show("L'image n'a pas pu être créée") }
                }
            }
            val entries = buildList {
                add(TeddMenuEntry("Rattacher à une compétence ou un objectif…", icon = "link", onClick = { attach = true }))
                if (map.skill_id != null) add(TeddMenuEntry("Mettre à jour depuis la compétence", icon = "refresh", onClick = {
                    model.refreshFromSkill { n -> snackbar.show(if (n == 0) "Rien de nouveau dans la compétence" else "$n élément${if (n > 1) "s" else ""} ajouté${if (n > 1) "s" else ""}") }
                }))
                add(TeddMenuEntry(if (model.revision) "Quitter la révision" else "Mode révision", icon = "layers", hint = "se tester", onClick = {
                    model.revision = !model.revision
                    if (model.revision) { model.collapseToLevel1(); model.select(model.root?.id) }
                }))
                add(TeddMenuEntry("Replier au niveau 1", icon = "fold", onClick = { model.collapseToLevel1() }))
                if (compact) {
                    add(TeddMenuEntry("Tout replier", icon = "fold", onClick = { model.collapseAll() }))
                    add(TeddMenuEntry("Tout déplier", icon = "unfold", onClick = { model.expandAll() }))
                    add(TeddMenuEntry("Chercher dans la carte", icon = "search", onClick = { model.searchOpen = true }))
                    if (TeddShare.shareImage != null) add(TeddMenuEntry("Exporter l'image", icon = "image", onClick = export))
                }
                add(TeddMenuEntry("Ajuster la vue", icon = "fit", kbd = if (compact) null else "Ctrl 0", onClick = { model.fitRequest++ }))
                add(TeddMenuEntry("Supprimer la carte", icon = "trash", danger = true, onClick = { model.deleteMap(map.id); snackbar.show("Carte supprimée") }))
            }
            TeddMenu(expanded = menu, onDismiss = { menu = false }, entries = entries, width = 320.dp, alignEnd = true)
            if (attach) AttachMenu(model, onClose = { attach = false })
        }
    }
}

/** « Rattacher à… » : une compétence, un objectif, ou rien. Les deux listes dans un seul menu, avec recherche. */
@Composable
private fun AttachMenu(model: MindMapModel, onClose: () -> Unit) {
    val map = model.map ?: return
    val entries = buildList {
        add(TeddMenuEntry("Aucune", section = "Compétence", icon = "minus", selected = map.skill_id == null, onClick = { model.setAttachments(null, map.objective_id) }))
        model.skills.filter { it.archived == 0L }.forEach { s ->
            add(TeddMenuEntry(s.name, section = "Compétence", icon = "trend", iconColor = parseHexColor(s.color) ?: TeddTokens.InkMuted, selected = map.skill_id == s.id, onClick = { model.setAttachments(s.id, map.objective_id) }))
        }
        add(TeddMenuEntry("Aucun", section = "Objectif", icon = "minus", selected = map.objective_id == null, onClick = { model.setAttachments(map.skill_id, null) }))
        model.objectives.filter { it.status == "active" }.forEach { o ->
            add(TeddMenuEntry(o.title, section = "Objectif", icon = "goals", iconColor = TeddTokens.ModGoals, selected = map.objective_id == o.id, onClick = { model.setAttachments(map.skill_id, o.id) }))
        }
    }
    TeddMenu(expanded = true, onDismiss = onClose, entries = entries, width = 380.dp, alignEnd = true, searchable = true, placeholder = "Chercher une compétence ou un objectif…")
}

@Composable
private fun SearchBox(model: MindMapModel, modifier: Modifier = Modifier) {
    Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        TeddField(
            label = "",
            value = model.search,
            onValueChange = { model.search = it },
            placeholder = "Chercher… (Entrée : suivant)",
            modifier = Modifier.weight(1f)
                .onPreviewKeyEvent { e ->
                    if (e.type == KeyEventType.KeyDown && (e.key == Key.Enter || e.key == Key.NumPadEnter)) { model.nextMatch(); true }
                    else if (e.type == KeyEventType.KeyDown && e.key == Key.Escape) { model.searchOpen = false; model.search = ""; true }
                    else false
                },
            trailingContent = {
                val n = model.matches.size
                if (model.search.isNotBlank()) Text(if (n == 0) "0" else "$n", style = TeddType.Caption, color = TeddTokens.InkMuted)
            },
        )
        TeddIconButton(onClick = { model.searchOpen = false; model.search = "" }) { TeddIcon(name = "x", size = 14.dp, color = TeddTokens.InkMuted) }
    }
}

/** Boutons de zoom en bas à droite, fil d'Ariane en haut, annulation de suppression, aide. */
@Composable
private fun CanvasOverlays(model: MindMapModel, camera: MindMapCamera, compact: Boolean) {
    val scope = rememberCoroutineScope()
    Box(Modifier.fillMaxSize()) {
        // Fil d'Ariane du nœud sélectionné : chaque maillon recentre.
        val selected = model.selectedId
        if (selected != null && !compact) {
            val path = model.layout.pathTo(selected)
            if (path.size > 1) Row(
                Modifier.align(Alignment.TopStart).padding(TeddSpace.S3)
                    .background(TeddTokens.Surface.copy(alpha = 0.92f), RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S1),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                path.forEachIndexed { i, id ->
                    val text = model.nodes.firstOrNull { it.id == id }?.text?.ifBlank { "…" } ?: "…"
                    Text(
                        text.take(28) + if (text.length > 28) "…" else "",
                        style = TeddType.Caption, color = if (i == path.lastIndex) TeddTokens.Ink else TeddTokens.InkMuted,
                        modifier = Modifier.clickable { model.select(id); model.centerRequest = id },
                    )
                    if (i < path.lastIndex) TeddIcon(name = "chevron-right", size = 12.dp, color = TeddTokens.InkSubtle, modifier = Modifier.padding(horizontal = 2.dp))
                }
            }
        }
        // Zoom.
        Column(Modifier.align(Alignment.BottomEnd).padding(TeddSpace.S3), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            val center = { androidx.compose.ui.geometry.Offset(camera.viewport.width / 2f, camera.viewport.height / 2f) }
            ZoomButton("zoom-in") { camera.zoomAt(center(), 1.25f) }
            ZoomButton("zoom-out") { camera.zoomAt(center(), 0.8f) }
            ZoomButton("fit") { model.fitRequest++ }
        }
        // Les gestes de base, rappelés tant que la carte est petite.
        if (!compact && model.totalCount < 15 && model.lastDeletion == null && !model.revision) {
            Text(
                "Touche un nœud · Tab : enfant · Maj+Tab : frère · Entrée : modifier · Espace : plier · Suppr : supprimer · double-clic : modifier",
                style = TeddType.Caption, color = TeddTokens.InkSubtle,
                modifier = Modifier.align(Alignment.BottomStart).padding(TeddSpace.S3),
            )
        }
        // Révision : révéler la branche suivante.
        if (model.revision) {
            TeddButton(
                text = "Révéler", onClick = { model.revealNext() }, kind = TeddButtonKind.Primary, size = TeddButtonSize.Md, icon = "eye",
                modifier = Modifier.align(Alignment.BottomCenter).padding(TeddSpace.S4),
            )
        }
        // Annuler une suppression, cinq secondes.
        val deletion = model.lastDeletion
        if (deletion != null) {
            LaunchedEffect(deletion) { delay(5000); if (model.lastDeletion === deletion) model.lastDeletion = null }
            Row(
                Modifier.align(if (model.revision) Alignment.TopCenter else Alignment.BottomCenter).padding(TeddSpace.S4)
                    .background(TeddTokens.Ink, RoundedCornerShape(TeddTokens.RMd)).padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S2),
                verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
            ) {
                Text("Branche supprimée", style = TeddType.Body, color = TeddTokens.Bg)
                Text("Annuler", style = TeddType.BodyStrong, color = TeddTokens.Lime, modifier = Modifier.clickable { model.undoDeletion() })
            }
        }
    }
}

@Composable
private fun ZoomButton(icon: String, onClick: () -> Unit) {
    Box(
        Modifier.width(40.dp).height(40.dp)
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) { TeddIcon(name = icon, size = 18.dp, color = TeddTokens.Ink) }
}

// ── Téléphone ─────────────────────────────────────────────────────────────────

@Composable
private fun CompactMap(
    model: MindMapModel,
    camera: MindMapCamera,
    painter: MindMapPainter,
    snackbar: TeddSnackbarState,
    openSource: (String, String) -> Unit,
    requestDelete: (String) -> Unit,
) {
    Column(Modifier.fillMaxSize()) {
        MapToolbar(model, camera, painter, snackbar, compact = true, onBack = { model.close() })
        if (model.searchOpen) SearchBox(model, Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S1))
        Box(Modifier.weight(1f).fillMaxWidth()) {
            MindMapCanvas(
                model, camera, LayoutMode.LeftToRight, painter, Modifier.fillMaxSize(),
                inlineEdit = false,
                onOpenNotes = { model.select(it); model.notesOpen = true },
                onRequestDelete = requestDelete,
            )
            CanvasOverlays(model, camera, compact = true)
        }
        // La barre d'actions sous le pouce, quand un nœud est sélectionné.
        val selected = model.selected
        Row(
            Modifier.fillMaxWidth().background(TeddTokens.Surface).border(1.dp, TeddTokens.Line).navigationBarsPadding().padding(horizontal = TeddSpace.S2, vertical = TeddSpace.S2),
            horizontalArrangement = Arrangement.SpaceEvenly,
        ) {
            if (selected == null) {
                ActionChip("plus", "Branche") { model.addChild(); model.notesOpen = true }
                Text("Touche un nœud pour le modifier · pince pour zoomer", style = TeddType.Caption, color = TeddTokens.InkMuted, modifier = Modifier.padding(TeddSpace.S2).weight(1f))
            } else {
                ActionChip("plus", "Enfant") { model.addChild(selected.id) ; model.notesOpen = true }
                if (selected.parent_id != null) ActionChip("arrow-down", "Frère") { model.addSibling(selected.id); model.notesOpen = true }
                ActionChip("notes", "Notes") { model.notesOpen = true }
                if (model.nodes.any { it.parent_id == selected.id }) ActionChip(if (selected.id in model.collapsed) "unfold" else "fold", if (selected.id in model.collapsed) "Déplier" else "Plier") { model.toggleCollapse(selected.id) }
                if (selected.parent_id != null) ActionChip("trash", "Supprimer") { requestDelete(selected.id) }
            }
        }
    }
    val selectedId = model.selectedId
    TeddBottomSheet(visible = model.notesOpen && selectedId != null, onDismiss = { model.closeNotes() }) {
        if (selectedId != null) MindMapNodePanel(model, selectedId, compact = true, onOpenSource = openSource, onRequestDelete = { model.notesOpen = false; requestDelete(it) }, onClose = { model.closeNotes() }, modifier = Modifier.fillMaxWidth().heightIn(max = 560.dp))
    }
}

@Composable
private fun ActionChip(icon: String, label: String, onClick: () -> Unit) {
    Column(
        Modifier.widthIn(min = 56.dp).heightIn(min = 48.dp).clickable(onClick = onClick).padding(horizontal = TeddSpace.S1, vertical = TeddSpace.S1),
        horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center,
    ) {
        TeddIcon(name = icon, size = 20.dp, color = TeddTokens.Ink)
        Text(label, style = TeddType.Caption, color = TeddTokens.InkMuted)
    }
}

// ── Dialogues ─────────────────────────────────────────────────────────────────

@Composable
private fun CreateMapDialog(model: MindMapModel, onClose: () -> Unit) {
    var title by remember { mutableStateOf("") }
    val close = rememberAfterFrame(onClose)
    Dialog(onDismissRequest = close, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier.width(440.dp)
                .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                .padding(TeddSpace.S6),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
        ) {
            Text("Nouvelle carte heuristique", style = TeddType.Title2, color = TeddTokens.Ink)
            TeddField(label = "Sujet central", value = title, onValueChange = { title = it }, placeholder = "Ce que tu veux comprendre")
            Text("Ou pars d'un gabarit :", style = TeddType.Caption, color = TeddTokens.InkMuted)
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TEMPLATES.forEach { (name, t, branches) ->
                    TeddButton(text = name, onClick = { model.createFromTemplate(title.ifBlank { t }, branches); close() }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm)
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(text = "Créer", onClick = { model.create(title); close() }, kind = TeddButtonKind.Primary, enabled = title.isNotBlank(), modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = close, kind = TeddButtonKind.Ghost)
            }
        }
    }
}

@Composable
private fun ConfirmDeleteDialog(weight: Int, onConfirm: () -> Unit, onClose: () -> Unit) {
    val confirm = rememberAfterFrame(onConfirm)
    val close = rememberAfterFrame(onClose)
    Dialog(onDismissRequest = close, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier.width(400.dp)
                .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                .padding(TeddSpace.S6),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
        ) {
            Text("Supprimer cette branche ?", style = TeddType.Title2, color = TeddTokens.Ink)
            Text("Elle emporte $weight élément${if (weight > 1) "s" else ""} : sous-branches et notes écrites dedans. Les notes de tes carnets restent à leur place.", style = TeddType.Body, color = TeddTokens.InkMuted)
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(text = "Supprimer", onClick = confirm, kind = TeddButtonKind.Primary, modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = close, kind = TeddButtonKind.Ghost)
            }
        }
    }
}
